import AtriumApi from "api";
import LineChart from "components/charts/LineChart";
import Loader from "components/loader/Loader";
import { useEffect, useMemo, useState } from "react";

const chartOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: false,
    },
  },
  markers: {
    size: 0,
    colors: "white",
    strokeColors: "#7551FF",
    strokeWidth: 3,
    strokeOpacity: 0.9,
    strokeDashArray: 0,
    fillOpacity: 1,
    // discrete: [],
    shape: "circle",
    radius: 2,
    offsetX: 0,
    offsetY: 0,
    showNullDataPoints: true,
  },
  tooltip: {
    theme: "dark",
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    type: "line",
    width: [5, 3],
    dashArray: [0, 8],
    colors: ["#6DA38C", "#CED7EE"],
  },
  xaxis: {
    type: "numeric",
    categories: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    type: "numeric",
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
      formatter: (value) => {
        return value.toString() + " %";
      },
    },
  },
  legend: {
    show: true,
    horizontalAlign: "left",
    markers: {
      offsetX: -5,
      strokeWidth: 0,
      size: 5,
    },
    labels: {
      colors: ["#A3AED0", "#A3AED0"],
    },
    itemMargin: {
      horizontal: 15,
    },
  },
  grid: {
    show: false,
  },
};

// const chartData = [
//   {
//     name: "This month",
//     data: [50, 64, 48, 66, 49, 68, 50, 64, 48, 66, 49, 68],
//   },
//   {
//     name: "Previous month",
//     data: [30, 40, 24, 46, 20, 46, 30, 40, 24, 46, 20, 46],
//   },
// ];

function AverageVisitDuration({ selectedCampusId }) {
  const [filter, setFilter] = useState("weekly");
  const [averageData, setAverageData] = useState([]);
  const [loading, setLoading] = useState(false);

  const newOptions = useMemo(() => {
    const getXAxisCategories = () => {
      if (!averageData?.chartData?.length) return [];

      if (filter === "weekly") {
        return ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
      }

      if (filter === "monthly") {
        // const daysInMonth = new Date().getDate();
        const today = new Date();
        const totalDays = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        ).getDate();
        return Array.from({ length: totalDays }, (_, i) => `${i + 1}`);
      }

      if (filter === "yearly") {
        return [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUN",
          "JUL",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
      }

      return [];
    };
    return {
      ...chartOptions,
      colors: ["#6DA38C", "#CED7EE"],
      xaxis: {
        ...chartOptions.xaxis,
        categories: getXAxisCategories(),
      },
    };
  }, [filter, averageData]);

  useEffect(() => {
    function buildQueryString() {
      let query = "";
      if (filter && filter !== "") {
        query += `filter=${filter}`;
      }
      if (selectedCampusId && selectedCampusId !== "all") {
        query += `${query ? "&" : ""}campusId=${selectedCampusId}`;
      }

      return query;
    }

    const getAverageVisitDuration = async () => {
      try {
        setLoading(true);
        const query = buildQueryString();
        const response = await AtriumApi.get(
          `/v1/visitduration${query ? `?${query}` : ""}`
        );
        if (response) {
          setLoading(false);
          setAverageData(response?.data?.data);
        }
      } catch (error) {
        console.log("error while fetching average visit duration", error);

        setLoading(false);
      }
    };

    getAverageVisitDuration();
  }, [filter, selectedCampusId]);

  // yesterday comparison value and show color red or green
  // const isNegative = averageData?.percentChange?.includes("-");
  const isNegative = String(averageData?.percentageChange || "").includes("-");

  return (
    <>
      <div className="flex-1 relative items-center rounded-[30px] p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
        {loading ? (
          <div className="flex h-full items-center justify-center w-full">
            <Loader />
          </div>
        ) : (
          <>
            {/* header */}
            <div className="flex">
              <div className="flex-1">
                <p className="text-gray-700">Average visit duration</p>
                <p>
                  <span className="text-2xl font-bold mr-2">
                    {averageData?.averageVisitDuration}
                  </span>
                  <span
                    className={isNegative ? "arrow-down" : "arrow-up"}
                  ></span>
                  <span
                    className={`text-[12px] font-bold ml-2 ${
                      isNegative ? "text-red-500" : "text-green-atrium"
                    }`}
                  >
                    {averageData?.percentageChange}
                  </span>
                </p>
              </div>
              <div>
                <select
                  className="bg-[transparent] h-12 text-sm text-gray-700 rounded-xl pr-4 outline-none rounded-2xl p-[8px] mr-4"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>

            {/* chart */}
            <div className="block h-[300px]">
              <div className="h-full w-full">
                {averageData?.chartData?.length > 0 ? (
                  <LineChart
                    chartData={averageData?.chartData}
                    chartOptions={newOptions}
                  />
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default AverageVisitDuration;
